import React, { createContext, useContext, useEffect, useState } from 'react';

interface ThemeColors {
  background: {
    primary: string;
    secondary: string;
  };
  text: {
    primary: string;
    secondary: string;
  };
  primary: string;
  success: string;
  warning: string;
  info: string;
  error: string;
}

// Função para validar cores hex
const isValidHexColor = (color: string): boolean => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
};

// Função para validar o tema
const isValidTheme = (theme: any): theme is ThemeColors => {
  if (!theme || typeof theme !== 'object') return false;

  // Validar background
  if (
    !theme.background ||
    !isValidHexColor(theme.background.primary) ||
    !isValidHexColor(theme.background.secondary)
  ) {
    console.error('Invalid background colors in theme:', theme.background);
    return false;
  }

  // Validar text
  if (
    !theme.text ||
    !isValidHexColor(theme.text.primary) ||
    !isValidHexColor(theme.text.secondary)
  ) {
    console.error('Invalid text colors in theme:', theme.text);
    return false;
  }

  // Validar primary
  if (!isValidHexColor(theme.primary)) {
    console.error('Invalid primary color in theme:', theme.primary);
    return false;
  }

  return true;
};

// Contexto do tema
const ThemeContext = createContext<ThemeColors | null>(null);

// Um tema de fallback simples caso o JSON falhe
const fallbackTheme: ThemeColors = {
  background: { primary: '#111111', secondary: '#222222' },
  text: { primary: '#EEEEEE', secondary: '#CCCCCC' },
  primary: '#BBBBBB',
  success: '#00E23F',
  warning: '#FFA500',
  info: '#0000FF',
  error: '#D70708',
};

const THEME_STORAGE_KEY = 'app_theme_cache';
const THEME_CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

interface CachedTheme {
  theme: ThemeColors;
  timestamp: number;
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeColors>(fallbackTheme);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Função para carregar tema do cache
  const loadThemeFromCache = (): ThemeColors | null => {
    try {
      const cached = localStorage.getItem(THEME_STORAGE_KEY);
      if (!cached) return null;

      const parsed: CachedTheme = JSON.parse(cached);
      const now = Date.now();

      // Verifica se o cache ainda é válido
      if (now - parsed.timestamp < THEME_CACHE_DURATION) {
        console.log('[ThemeContext] Tema carregado do cache');
        return parsed.theme;
      }

      // Cache expirado, remove
      localStorage.removeItem(THEME_STORAGE_KEY);
      return null;
    } catch (error) {
      console.error('[ThemeContext] Erro ao carregar cache:', error);
      localStorage.removeItem(THEME_STORAGE_KEY);
      return null;
    }
  };

  // Função para salvar tema no cache
  const saveThemeToCache = (themeData: ThemeColors) => {
    try {
      const cached: CachedTheme = {
        theme: themeData,
        timestamp: Date.now(),
      };
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(cached));
      console.log('[ThemeContext] Tema salvo no cache');
    } catch (error) {
      console.error('[ThemeContext] Erro ao salvar cache:', error);
    }
  };

  useEffect(() => {
    const loadTheme = async () => {
      try {
        // Primeiro tenta carregar do cache
        const cachedTheme = loadThemeFromCache();
        if (cachedTheme && isValidTheme(cachedTheme)) {
          setTheme(cachedTheme);
          setIsLoading(false);

          // Carrega em background para atualizar se necessário
          loadThemeFromServer();
          return;
        }

        // Se não tem cache válido, carrega do servidor
        await loadThemeFromServer();
      } catch (err) {
        console.error('[ThemeContext] Erro crítico ao carregar tema:', err);
        setError(err as Error);
        setTheme(fallbackTheme);
        setIsLoading(false);
      }
    };

    const loadThemeFromServer = async () => {
      try {
        // Adiciona timestamp para evitar cache do navegador
        const response = await fetch(`/assets/theme/colors.json?t=${Date.now()}`, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          },
        });

        if (!response.ok) {
          throw new Error(`Falha ao carregar o tema: ${response.status}`);
        }

        const data = await response.json();
        const mergedTheme = {
          ...data,
          success: '#00E23F',
          warning: '#FFA500',
          info: '#0000FF',
          error: '#D70708',
        };

        if (isValidTheme(mergedTheme)) {
          setTheme(mergedTheme);
          saveThemeToCache(mergedTheme);
          console.log('[ThemeContext] Tema carregado do servidor com sucesso');
        } else {
          throw new Error('Formato do tema inválido');
        }
      } catch (err) {
        console.error('[ThemeContext] Erro ao carregar tema do servidor:', err);
        // Não sobrescreve o tema atual se já temos um válido
        if (!isValidTheme(theme)) {
          setTheme(fallbackTheme);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, []);

  // Listener para mudanças no storage (outras abas)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY && e.newValue) {
        try {
          const parsed: CachedTheme = JSON.parse(e.newValue);
          if (isValidTheme(parsed.theme)) {
            setTheme(parsed.theme);
            console.log('[ThemeContext] Tema atualizado via storage event');
          }
        } catch (error) {
          console.error('[ThemeContext] Erro ao processar storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  if (isLoading) {
    // Retorna o tema atual (fallback ou cache) ao invés de null
    return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
  }

  if (error) {
    console.error('Erro ao carregar tema:', error);
    // Mesmo com erro, retorna o tema fallback
    return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
  }

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext deve ser usado dentro de um ThemeProvider');
  }
  return context;
};

// getCurrentTheme pode ser removido se não for usado fora de hooks/componentes
// Se precisar, teria que ser refatorado para lidar com o estado assíncrono
// export const getCurrentTheme = ...
